/* ──────────────────────────────────────────────────────────────
   SessionContext.tsx · the real signed-in user/org from platform /v1/me
   Replaces all hardcoded "Priya Raman / Madras Filter Co." mock identity.
   ────────────────────────────────────────────────────────────── */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getCurrentUser } from '../services/auth/auth';

export interface SessionUser {
  id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
}
export interface SessionOrg {
  id?: string;
  name?: string;
  status?: string;
  /** salon | hosp | null — drives the vertical-aware Setup Guide + intent view. */
  vertical?: string | null;
  /** Onboarding state from platform (metadata.setup) so the console can gate. */
  setup?: { completed_at: string | null; steps: Record<string, string> };
}

interface SessionValue {
  user: SessionUser | null;
  org: SessionOrg | null;
  role: string | null;
  /** Platform-scoped roles (orgId=null), e.g. ['platform_admin']. */
  platformRoles: string[];
  isPlatformAdmin: boolean;
  /** Flattened platform scopes for the active role set (from /v1/me). Action
   *  gating (e.g. billing:write) reads these — the JWT is httpOnly, unreadable. */
  scopes: string[];
  /** True iff the session carries the given scope. */
  can: (scope: string) => boolean;
  /** Entitled product slugs (e.g. ['voice','salon']) from /v1/me. */
  products: string[];
  loading: boolean;
  fullName: string;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue>({
  user: null, org: null, role: null, platformRoles: [], isPlatformAdmin: false,
  scopes: [], can: () => false, products: [], loading: true, fullName: '', refresh: async () => {},
});

// Maps the platform role slug to a human label for the sidebar.
function roleLabel(role: string | null): string {
  switch (role) {
    case 'org_admin': return 'Owner';
    case 'admin': return 'Admin';
    case 'platform_admin': return 'Platform Admin';
    case 'user': return 'Member';
    default: return role ? role.replace(/_/g, ' ') : '';
  }
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [org, setOrg] = useState<SessionOrg | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [platformRoles, setPlatformRoles] = useState<string[]>([]);
  const [scopes, setScopes] = useState<string[]>([]);
  const [products, setProducts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await getCurrentUser();
      const d = res?.data ?? res;
      setUser(d?.user ?? null);
      setOrg(d?.org ?? null);
      // The active-org membership role (org_admin | admin | user). The org shown
      // is the active membership, so its role is memberships[0] post-resolution.
      setRole(d?.memberships?.[0]?.role ?? null);
      setPlatformRoles(Array.isArray(d?.platform_roles) ? d.platform_roles : []);
      setScopes(Array.isArray(d?.scopes) ? d.scopes : []);
      // Entitled product slugs — lets the console know if voice/salon are on.
      setProducts(Array.isArray(d?.products_entitled) ? d.products_entitled.map((p: any) => p.slug) : []);
    } catch {
      setUser(null); setOrg(null); setRole(null); setPlatformRoles([]); setScopes([]); setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  const isPlatformAdmin = platformRoles.includes('platform_admin');
  const can = useCallback((scope: string) => scopes.includes(scope), [scopes]);

  return (
    <SessionContext.Provider value={{ user, org, role, platformRoles, isPlatformAdmin, scopes, can, products, loading, fullName, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export const useSession = () => useContext(SessionContext);
export { roleLabel };
